import * as React from "react";
import { Form, Query } from "../../interfaces";
import {
  Typography,
  List,
  ListItem,
  Grid,
  Box,
  Button,
  IconButton,
} from "@mui/material";
import { historicalFormSelectChoices } from "../../refdata/constants";
import ClearReset from "../../containers/ClearReset";
import { ArrowLeftIcon, ArrowRightIcon } from "@mui/x-date-pickers";
import SelectFilterField from "../../containers/SelectFilterField";
import { yearChoice } from "../../common/services/Utilities";
import Sections from "../Sections";

export interface HistoricalDataForm extends Form {
  selectedReport: string;
  displayFields: string[];
  appStateDisplayFields: any;
}
export default function HistoricalSearchForm({
  appState,
  searchPanelExpand,
  endYear,
  setSearchPanelExpand,
  updateDisplay,
  updateLocations,
}: {
  appState: Query;
  searchPanelExpand: boolean;
  endYear: number;
  setSearchPanelExpand: (updatedSearchPanelExpand: boolean) => void;
  updateDisplay: (report: string) => void;
  updateLocations: (location: number[]) => void;
}) {
  const yearRange = yearChoice(
    1934,
    endYear ? endYear : new Date().getFullYear(),
  );
  const [resetLocations, setResetLocations] = React.useState(false);

  const [prevLocations, setPrevLocations] = React.useState(
    appState.filters.selectedStates,
  );

  const handleCollapse = () => {
    setSearchPanelExpand(!searchPanelExpand);
  };

  //sets locations for SIS and resets to previous
  React.useEffect(() => {
    if (
      appState.filters.selectedReport === "SIS" &&
      JSON.stringify(appState.filters.selectedStates) !== "[0]"
    ) {
      setResetLocations(true);
      setPrevLocations(appState.filters.selectedStates);
      updateLocations([0]);
    } else if (appState.filters.selectedReport !== "SIS" && resetLocations) {
      setResetLocations(false);
      updateLocations(prevLocations);
    }
  }, [
    appState.filters.selectedReport,
    appState.filters.selectedStates,
    prevLocations,
    resetLocations,
    updateLocations,
  ]);

  const HistoricalDataList = searchPanelExpand ? (
    <>
      <Typography style={{ padding: 0, marginBottom: "1rem" }} variant="h2">
        Report Parameters
      </Typography>
      <Box
        id="search-form-box-top"
        style={{ borderTopRightRadius: 5, borderTopLeftRadius: 5 }}
        sx={{
          bgcolor: "secondary.main",
          justifyContent: "center",
          border: "1px solid #ACC8DF",
        }}
      >
        <Button
          id="hide-search-panel-button"
          sx={(theme) => ({
            "marginTop": 0,
            "width": "100%",
            "textTransform": "capitalize",
            "fontSize": 14,
            "fontWeight": 400,
            "color": theme.palette.primary.main,
            "borderBottomRightRadius": 0,
            "borderBottomLeftRadius": 0,
            "transition": "none",
            "borderBottom": "1px solid #acc8df",
            "&:hover, &:focus": {
              color: "#ffffff",
              backgroundColor: theme.palette.primary.main,
            },
            "&:active": {
              color: theme.palette.primary.main,
              backgroundColor: "#ffffff",
            },
          })}
          onClick={handleCollapse}
          startIcon={<ArrowLeftIcon />}
        >
          Hide search panel
        </Button>
        <List
          sx={(theme) => ({
            "marginBottom": "1rem",
            "& li#select-report-li": {
              "paddingBottom": 0,
              "& .MuiFormControl-marginDense": {
                marginBottom: 0,
              },
              "& div.MuiGrid-item": {
                padding: 0,
              },
            },
            "& li#locations-li": {
              paddingBottom: 0,
              paddingTop: 0,
            },
            "& li#start-end-date-li": {
              "& div.MuiGrid-item": {
                "paddingTop": 0,
                "paddingBottom": 0,
                "& div .MuiFormControl-marginDense": {
                  marginTop: 0,
                  marginBottom: 0,
                },
              },
            },
            "& li": {
              "display": "block",
              "& > div": {
                marginTop: 0,
                marginBottom: 0,
              },
            },
          })}
        >
          <ListItem id="select-report-li" sx={{ marginBottom: 1 }}>
            <SelectFilterField
              currentValue={appState.filters.selectedReport}
              fieldName="selectedReport"
              label={"Choose a Report"}
              options={historicalFormSelectChoices}
              noEmptyVal
              onChange={(e: any) => updateDisplay(e.target.value)}
            />
          </ListItem>
          <ListItem id="start-end-date-li">
            <Grid container spacing={2}>
              <Grid size={6}>
                <SelectFilterField
                  currentValue={appState.filters.startDate.toString()}
                  fieldName="startDate"
                  label={"Start Year"}
                  options={yearRange}
                  noEmptyVal
                />
              </Grid>
              <Grid size={6}>
                <SelectFilterField
                  currentValue={appState.filters.endDate?.toString()}
                  fieldName="endDate"
                  label={"End Year"}
                  options={yearRange}
                  noEmptyVal
                />
              </Grid>
            </Grid>
          </ListItem>
          <ListItem id="locations-li">
            <Sections
              updateReport
              disabledText={
                appState.filters.selectedReport === "SIS"
                  ? "State-level institution count is unavailable."
                  : undefined
              }
              selectedReport={appState.filters.selectedReport}
              appState={appState}
              filterField="selectedStates"
              displayFields={
                appState.filters.selectedStates
                  ? appState.filters.selectedStates
                  : [undefined]
              }
              popupWidth="large"
            />
          </ListItem>
        </List>
      </Box>
      <Box
        style={{ borderBottomRightRadius: 5, borderBottomLeftRadius: 5 }}
        sx={{
          boxShadow: "inset 0px 5px 5px -5px #1A4480",
          bgcolor: "#ecf3fb",
          border: 1,
          marginTop: -1,
          justifyContent: "center",
          borderColor: "#ACC8DF",
          paddingTop: 1,
          paddingBottom: 0.5,
        }}
      >
        <ListItem>
          <Typography variant="h4" style={{ paddingBottom: 0 }}>
            Modify Report Filters
          </Typography>
        </ListItem>
        <ListItem id="clear-reset-li">
          <Grid container spacing={2} width="100%">
            <Grid size={6}>
              <ClearReset
                buttonLabel="Default Filters"
                report={appState.filters.selectedReport}
                buttonType="reset"
              />
            </Grid>

            <Grid size={6}>
              <ClearReset buttonLabel="Clear Filters" buttonType="clear" />
            </Grid>
          </Grid>
        </ListItem>
        <ListItem id="report-filters-li">
          <Sections
            updateReport
            appState={appState}
            displayFields={
              appState.displayFields ? appState.displayFields : [""]
            }
            selectedReport={appState.filters.selectedReport}
          />
        </ListItem>
      </Box>
    </>
  ) : (
    <IconButton
      id="show-search-panel-button"
      sx={(theme) => ({
        "borderRadius": 4,
        "backgroundColor": theme.palette.secondary.main,
        "color": theme.palette.primary.main,
        "border": "1px solid #acc8df",
        "transition": "none",
        "height": 40,
        "width": 40,
        "padding": 0,
        "&:hover, &:focus": {
          color: "#ffffff",
          backgroundColor: theme.palette.primary.main,
        },
        "&:active": {
          color: theme.palette.primary.main,
          backgroundColor: "#ffffff",
        },
      })}
      onClick={handleCollapse}
      size="large"
    >
      <ArrowRightIcon />
    </IconButton>
  );

  return HistoricalDataList;
}
